import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { adminAuthService } from '../../services/adminAuthService';
import { accessRequestService } from '../../services/accessRequestService';
import { verificationService } from '../../services/verificationService';
import ProfileApprovalCard from './components/ProfileApprovalCard';
import BulkApprovalPanel from './components/BulkApprovalPanel';
import EmergencyOverridePanel from './components/EmergencyOverridePanel';
import AuditLogPanel from './components/AuditLogPanel';

const SUPER_ADMIN_EMAIL = 'jonathan@blindrecruit.com';

const SuperAdminPortal = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminSession, setAdminSession] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Dashboard state
  const [pendingProfiles, setPendingProfiles] = useState([]);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPending: 0,
    approvedToday: 0,
    rejectedToday: 0,
    avgResponseTime: '0h'
  });
  const [activeTab, setActiveTab] = useState('approvals');
  const [auditLogs, setAuditLogs] = useState([]);

  // Check if admin is already authenticated
  useEffect(() => {
    const session = adminAuthService?.getCurrentSession();
    if (session && session?.email === SUPER_ADMIN_EMAIL) {
      setIsAuthenticated(true);
      setAdminSession(session);
      loadDashboardData();
    }
  }, []);

  // Handle admin authentication
  const handleAdminLogin = async (e) => {
    e?.preventDefault();
    setAuthError('');
    setIsVerifying(true);

    try {
      // Validate email
      if (emailInput?.toLowerCase() !== SUPER_ADMIN_EMAIL?.toLowerCase()) {
        setAuthError('Access denied. This portal is restricted to authorized super admin only.');
        setIsVerifying(false);
        return;
      }

      // Sign in with admin_accounts
      console.log('🔐 Attempting admin authentication for:', emailInput);
      const adminAccount = await adminAuthService?.signIn(emailInput, passwordInput);
      
      if (adminAccount) {
        console.log('✅ Admin authentication successful:', adminAccount?.email);
        setIsAuthenticated(true);
        setAdminSession({
          id: adminAccount?.id,
          email: adminAccount?.email,
          fullName: adminAccount?.full_name,
          role: adminAccount?.role
        });
        await loadDashboardData();
      } else {
        setAuthError('Authentication failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('❌ Authentication error:', error);
      setAuthError(error?.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Load dashboard data
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all requests for stats calculation
      const allRequests = await accessRequestService?.getAll();
      
      // Fetch only pending profiles using the admin_pending_approvals view
      // This view automatically filters for status = 'pending'
      const pending = await accessRequestService?.getPendingApprovals();
      
      // Calculate today's date at midnight for accurate comparison
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      // Count approved today - check if reviewedAt exists and is today
      const approvedToday = allRequests?.filter(r => {
        if (r?.status !== 'approved' || !r?.reviewedAt) return false;
        const reviewedDate = new Date(r?.reviewedAt);
        reviewedDate?.setHours(0, 0, 0, 0);
        return reviewedDate?.getTime() === today?.getTime();
      })?.length || 0;
      
      // Count rejected today - check if reviewedAt exists and is today
      const rejectedToday = allRequests?.filter(r => {
        if (r?.status !== 'rejected' || !r?.reviewedAt) return false;
        const reviewedDate = new Date(r?.reviewedAt);
        reviewedDate?.setHours(0, 0, 0, 0);
        return reviewedDate?.getTime() === today?.getTime();
      })?.length || 0;

      setPendingProfiles(pending);
      setStats({
        totalPending: pending?.length,
        approvedToday,
        rejectedToday,
        avgResponseTime: '2.5h'
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      alert('Failed to load dashboard data: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle profile approval
  const handleApprove = async (profileId) => {
    try {
      // Verify admin is authenticated
      if (!adminSession?.id) {
        alert('❌ Authentication required. Please sign in with your admin account.');
        return;
      }

      // Get profile details before approval
      const profile = pendingProfiles?.find(p => p?.id === profileId);
      
      console.log('🔵 Approving profile:', { profileId, profile, adminId: adminSession?.id });
      
      const result = await verificationService?.approveRequest(
        profileId,
        'Approved by super admin',
        adminSession?.id,
        adminSession?.fullName || 'Super Admin'
      );
      
      console.log('✅ Approval result:', result);
      console.log('✅ Updated status:', result?.status);
      
      // Reload dashboard to reflect changes
      await loadDashboardData();
      addAuditLog('Approved profile', `${profile?.fullName || 'Unknown'} (${profile?.role || 'Unknown role'})`);
      
      // Show success message
      alert(`✓ Profile approved successfully!\n\nUser: ${profile?.fullName}\nRole: ${profile?.role}\nNew Status: ${result?.status}\n\nThe profile has been moved to the ${profile?.role === 'candidate' ? 'candidates' : 'employers'} table and will receive access credentials.`);
    } catch (error) {
      console.error('❌ Error approving profile:', error);
      alert('Failed to approve profile: ' + (error?.message || JSON.stringify(error)));
    }
  };

  // Handle profile rejection
  const handleReject = async (profileId, reason) => {
    try {
      // Verify admin is authenticated
      if (!adminSession?.id) {
        alert('❌ Authentication required. Please sign in with your admin account.');
        return;
      }

      // Get profile details before rejection
      const profile = pendingProfiles?.find(p => p?.id === profileId);
      
      console.log('🔵 Rejecting profile:', { profileId, profile, reason, adminId: adminSession?.id });
      
      const result = await verificationService?.rejectRequest(
        profileId,
        reason || 'Rejected by super admin',
        'Rejected by super admin',
        adminSession?.id
      );
      
      console.log('✅ Rejection result:', result);
      console.log('✅ Updated status:', result?.status);
      
      // Reload dashboard to reflect changes
      await loadDashboardData();
      addAuditLog('Rejected profile', `${profile?.fullName || 'Unknown'} - Reason: ${reason || 'No reason provided'}`);
      
      // Show success message
      alert(`✓ Profile rejected successfully!\n\nUser: ${profile?.fullName}\nReason: ${reason || 'No reason provided'}\nNew Status: ${result?.status}\n\nThe profile has been marked as rejected and removed from pending approvals.`);
    } catch (error) {
      console.error('❌ Error rejecting profile:', error);
      alert('Failed to reject profile: ' + (error?.message || JSON.stringify(error)));
    }
  };

  // Handle bulk approval
  const handleBulkApprove = async () => {
    if (selectedProfiles?.length === 0) return;
    
    try {
      for (const profileId of selectedProfiles) {
        await verificationService?.approveRequest(
          profileId,
          'Bulk approved by super admin',
          adminSession?.id,
          adminSession?.fullName || 'Super Admin'
        );
      }
      setSelectedProfiles([]);
      await loadDashboardData();
      addAuditLog('Bulk approved profiles', selectedProfiles?.join(', '));
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('Failed to bulk approve: ' + error?.message);
    }
  };

  // Handle bulk rejection
  const handleBulkReject = async (reason) => {
    if (selectedProfiles?.length === 0) return;
    
    try {
      for (const profileId of selectedProfiles) {
        // Get profile details
        const profile = pendingProfiles?.find(p => p?.id === profileId);
        
        await verificationService?.rejectRequest(
          profileId,
          reason || 'Bulk rejected by super admin',
          'Bulk rejected by super admin',
          adminSession?.id
        );
      }
      setSelectedProfiles([]);
      await loadDashboardData();
      addAuditLog('Bulk rejected profiles', selectedProfiles?.join(', '));
    } catch (error) {
      console.error('Error bulk rejecting:', error);
      alert('Failed to bulk reject: ' + error?.message);
    }
  };

  // Add audit log
  const addAuditLog = (action, details) => {
    const log = {
      id: Date.now(),
      action,
      details,
      timestamp: new Date()?.toISOString(),
      user: SUPER_ADMIN_EMAIL
    };
    setAuditLogs(prev => [log, ...prev]);
  };

  // Filter profiles - Move this before functions that use it
  const filteredProfiles = pendingProfiles?.filter(profile => {
    const matchesSearch = !searchQuery || 
      profile?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      profile?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      profile?.companyName?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return matchesSearch;
  });

  // Toggle profile selection
  const toggleProfileSelection = (profileId) => {
    setSelectedProfiles(prev => 
      prev?.includes(profileId) 
        ? prev?.filter(id => id !== profileId)
        : [...prev, profileId]
    );
  };

  // Select all profiles
  const selectAllProfiles = () => {
    if (selectedProfiles?.length === filteredProfiles?.length) {
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles(filteredProfiles?.map(p => p?.id));
    }
  };

  // Render authentication form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
              <Icon name="shield" className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Super Admin Portal</h1>
            <p className="text-gray-600">Secure access for authorized administrators</p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email
              </label>
              <Input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e?.target?.value)}
                placeholder="jonathan@blindrecruit.com"
                required
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <Input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e?.target?.value)}
                placeholder="Enter your password"
                required
                className="w-full"
              />
            </div>

            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {authError}
              </div>
            )}

            <Button
              type="submit"
              disabled={isVerifying}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 rounded-lg transition-all duration-200"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center">
                  <Icon name="loader" className="animate-spin mr-2" />
                  Verifying...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800 font-medium mb-2">🔐 Admin Credentials:</p>
            <p className="text-xs text-blue-700">Email: jonathan@blindrecruit.com</p>
            <p className="text-xs text-blue-700">Password: AdminBlind2026!</p>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50 elevation-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-error to-warning">
                <Icon name="ShieldAlert" size={24} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Super Admin Portal</h1>
                <p className="text-sm text-muted-foreground">Logged in as {SUPER_ADMIN_EMAIL}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={loadDashboardData}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="LogOut"
                onClick={() => {
                  setIsAuthenticated(false);
                  setEmailInput('');
                  setPasswordInput('');
                }}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-warning/10 rounded-xl p-4 border border-warning/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Approvals</p>
                  <p className="text-3xl font-bold text-warning mt-1">{stats?.totalPending}</p>
                </div>
                <Icon name="Clock" size={32} color="#F59E0B" />
              </div>
            </div>
            <div className="bg-success/10 rounded-xl p-4 border border-success/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved Today</p>
                  <p className="text-3xl font-bold text-success mt-1">{stats?.approvedToday}</p>
                </div>
                <Icon name="CheckCircle" size={32} color="#10B981" />
              </div>
            </div>
            <div className="bg-error/10 rounded-xl p-4 border border-error/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejected Today</p>
                  <p className="text-3xl font-bold text-error mt-1">{stats?.rejectedToday}</p>
                </div>
                <Icon name="XCircle" size={32} color="#EF4444" />
              </div>
            </div>
            <div className="bg-primary/10 rounded-xl p-4 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Response Time</p>
                  <p className="text-3xl font-bold text-primary mt-1">{stats?.avgResponseTime}</p>
                </div>
                <Icon name="Zap" size={32} color="#3B82F6" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('approvals')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'approvals' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Profile Approvals
            </button>
            <button
              onClick={() => setActiveTab('bulk')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'bulk' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Bulk Actions
            </button>
            <button
              onClick={() => setActiveTab('emergency')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'emergency' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Emergency Override
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-6 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'audit' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Audit Logs
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'approvals' && (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  iconName="Search"
                />
              </div>
              {selectedProfiles?.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="CheckCircle"
                    onClick={handleBulkApprove}
                  >
                    Approve Selected ({selectedProfiles?.length})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="XCircle"
                    onClick={() => handleBulkReject('Bulk rejected')}
                  >
                    Reject Selected
                  </Button>
                </div>
              )}
            </div>

            {/* Select All */}
            {filteredProfiles?.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={selectedProfiles?.length === filteredProfiles?.length}
                  onChange={selectAllProfiles}
                  className="w-4 h-4 rounded border-border"
                />
                <span>Select all {filteredProfiles?.length} profiles</span>
              </div>
            )}

            {/* Profile Cards */}
            {loading ? (
              <div className="text-center py-12">
                <Icon name="Loader" size={32} className="animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground mt-4">Loading profiles...</p>
              </div>
            ) : filteredProfiles?.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <Icon name="CheckCircle" size={48} className="mx-auto text-success mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">All Caught Up!</h3>
                <p className="text-muted-foreground">No pending profiles require approval at this time.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProfiles?.map((profile) => (
                  <ProfileApprovalCard
                    key={profile?.id}
                    profile={profile}
                    isSelected={selectedProfiles?.includes(profile?.id)}
                    onToggleSelect={() => toggleProfileSelection(profile?.id)}
                    onApprove={() => handleApprove(profile?.id)}
                    onReject={(reason) => handleReject(profile?.id, reason)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'bulk' && (
          <BulkApprovalPanel
            profiles={filteredProfiles}
            selectedProfiles={selectedProfiles}
            onBulkApprove={handleBulkApprove}
            onBulkReject={handleBulkReject}
            onSelectAll={selectAllProfiles}
          />
        )}

        {activeTab === 'emergency' && (
          <EmergencyOverridePanel
            onOverride={(action) => {
              addAuditLog('Emergency override', action);
              loadDashboardData();
            }}
          />
        )}

        {activeTab === 'audit' && (
          <AuditLogPanel logs={auditLogs} />
        )}
      </div>
    </div>
  );
};

export default SuperAdminPortal;